/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import React from "react";

import { bem, BEM } from "../../../shared/BEM";
import { useFormElement } from "../../../shared/hooks/useFormElement";
import { FormFieldBase } from "../../../shared/FormModel/elements/FormFieldBase";
import { applyInlineElementMods } from "./shared";

/**
 * Component props
 */
export interface IFormFieldWrapperComponentProps<TElement extends FormFieldBase<any>> {
	element: TElement,
	render: (
		state: ReturnType<TElement["getState"]>,
		baseStyle: BEM
	) => React.ReactElement;
}

/**
 * Form field wrapper
 *
 * @param props Component props
 */
export function FormFieldWrapperComponent<TElement extends FormFieldBase<any>>(props: IFormFieldWrapperComponentProps<TElement>) {

	const state = useFormElement(props.element);

	const stlMods = [
		"type-" + state.type,
		"style-" + (state.config.style || "default"),
		"v-align-" + (state.config.verticalAlign || "top")
	];

	const labelMods = [
		"align-" + (state.config.labelAlign || "left"),
	];

	if (state.pristine) {
		stlMods.push("pristine");
	}

	if (state.touched) {
		stlMods.push("touched");
	}

	if (state.config.required) {
		stlMods.push("required");
	}

	if (state.config.readOnly) {
		stlMods.push("readonly");
	}

	if (state.valid) {
		stlMods.push("valid");
	} else {
		stlMods.push("invalid");
	}

	applyInlineElementMods(state.config, stlMods);

	const stl = bem("form-field", "", stlMods);

	return <label className={stl.str}>
		{ state.config.label ?
		<span className={stl.el("label").mod(labelMods).str}>
			<span className={stl.el("label-contents").str}>{state.config.label}</span>
			{ state.config.required ?
			<span className={stl.el("label-required").str}>*</span>
			: null }
		</span>
		: null }
		<span className={stl.el("field").str}>
			{props.render(state, stl)}
		</span>
	</label>;

}
